import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Eye, EyeOff, ShieldCheck, FileText, Truck, Mail, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const LOGO_SRC = "/login-logo-orange.png?v=3";
const WHATSAPP_URL = "https://wa.me/447578850395";
const SUPPORT_EMAIL = "vicky@hintonhygiene.co.uk";
const PRIVACY_URL = "https://www.hintonhygiene.co.uk/privacy-policy/";
const REMEMBERED_EMAIL_KEY = "hh_remembered_email";

const FEATURES = [
  { icon: Truck, title: "Collection history", text: "View your scheduled and completed collections." },
  { icon: FileText, title: "Compliance documents", text: "Download your WTN, HWCN and Duty of Care documents." },
  { icon: ShieldCheck, title: "Invoices and payments", text: "View your invoices and payment status in one place." },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(REMEMBERED_EMAIL_KEY) || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const hadRememberedEmail = useRef(false);
  if (hadRememberedEmail.current === false && email) hadRememberedEmail.current = true;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    const res = await login(email.trim(), password, remember);
    setLoading(false);
    if (res.ok) {
      try {
        if (remember) localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
        else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      } catch (storageErr) {
        console.warn("Could not persist remembered email:", storageErr);
      }
      toast.success(`Welcome back${res.user?.full_name ? ", " + res.user.full_name : ""}`);
      navigate("/dashboard", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white lg:grid lg:grid-cols-2">
      {/* Brand panel (desktop only) */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[color:var(--hh-ink)] p-12 text-white lg:flex">
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{ background: "radial-gradient(600px circle at 20% 15%, rgba(245,166,35,0.55), transparent 45%), radial-gradient(500px circle at 80% 85%, rgba(229,143,14,0.4), transparent 45%)" }}
        />
        <div className="relative">
          <img src={LOGO_SRC} alt="Hinton Hygiene" style={{ height: 58, width: "auto", objectFit: "contain" }} />
        </div>
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--hh-amber)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--hh-amber)]" /> Trusted by businesses across Nottingham
          </span>
          <h1 className="mt-5 font-display text-[2.6rem] font-bold leading-[1.08] tracking-tight">
            Your Trusted Hygiene Partner
          </h1>
          <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/70">
            Sanitary, Sharps &amp; Hygiene Waste Collection in Nottingham.
          </p>
          <div className="mt-9 space-y-3">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-3.5 rounded-xl border border-white/10 bg-white/[0.04] p-3.5">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[color:var(--hh-amber)]/15 text-[color:var(--hh-amber)]">
                  <f.icon className="h-[18px] w-[18px]" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-white">{f.title}</div>
                  <div className="text-[13px] text-white/60">{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-white/45">
          <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--hh-amber)]/70" />
          Secure access · your data stays private to your account
        </div>
      </div>

      {/* Form column */}
      <div className="relative flex min-h-screen flex-col px-5 py-8 sm:px-10 lg:min-h-0">
        <div className="flex flex-1 flex-col justify-center">
          <div className="mx-auto w-full max-w-md py-6">
            {/* Mobile logo */}
            <div className="mb-8 flex justify-center lg:hidden">
              <img src={LOGO_SRC} alt="Hinton Hygiene" style={{ height: 46, width: "auto", objectFit: "contain" }} />
            </div>

            <h2 className="font-display text-[26px] font-semibold leading-tight text-[color:var(--hh-ink)]">
              Welcome to your customer portal
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Sign in to securely manage your Hinton Hygiene services.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus={!hadRememberedEmail.current}
                  placeholder="you@company.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-11 hh-focus"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    autoFocus={hadRememberedEmail.current}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11 pr-11 hh-focus"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[color:var(--hh-ink)]"
                    data-testid="toggle-password-visibility"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="remember" className="flex cursor-pointer select-none items-center gap-2 text-sm text-[color:var(--hh-slate)]">
                  <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me-checkbox" />
                  Remember me
                </label>
                <button type="button" onClick={() => setForgotOpen(true)} className="text-sm font-medium text-[color:var(--hh-amber-600)] hover:underline" data-testid="forgot-password-link">
                  Forgot your password?
                </button>
              </div>

              {error && (
                <div data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="h-11 w-full rounded-xl bg-[hsl(var(--primary))] text-base font-semibold text-white transition-transform hover:bg-[var(--hh-amber-600)] active:scale-[0.99]"
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : "Sign in"}
              </Button>
            </form>

            {/* Support section */}
            <div className="mt-8 rounded-xl border border-[color:var(--hh-line)] bg-[var(--hh-band)] p-4" data-testid="support-section">
              <div className="text-sm font-semibold text-[color:var(--hh-ink)]">Need help accessing your account?</div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[color:var(--hh-amber-600)] hover:underline"
              >
                <MessageCircle className="h-4 w-4" /> Message us on WhatsApp: 07578 850395
              </a>
              <div className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 text-[color:var(--hh-amber-600)]" />
                <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:underline" data-testid="support-email-link">{SUPPORT_EMAIL}</a>
              </div>
            </div>

            {/* Mobile feature information (below the form) */}
            <div className="mt-10 lg:hidden" data-testid="mobile-features">
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--hh-amber-600)]">Trusted by businesses across Nottingham</span>
              <h3 className="mt-2 font-display text-xl font-semibold text-[color:var(--hh-ink)]">Your Trusted Hygiene Partner</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">Sanitary, Sharps &amp; Hygiene Waste Collection in Nottingham.</p>
              <div className="mt-5 space-y-3">
                {FEATURES.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-[color:var(--hh-line)] bg-white p-3.5">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]">
                      <f.icon className="h-[18px] w-[18px]" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-[color:var(--hh-ink)]">{f.title}</div>
                      <div className="text-[13px] text-muted-foreground">{f.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Discreet footer */}
        <footer className="mx-auto w-full max-w-md pt-6 text-center text-xs text-muted-foreground" data-testid="login-footer">
          Hinton Hygiene Ltd <span className="px-1">•</span> Licensed Waste Carrier: CBDU628230 <span className="px-1">•</span>{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--hh-amber-600)] hover:underline" data-testid="privacy-policy-link">Privacy Policy</a>
        </footer>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reset your password</DialogTitle>
            <DialogDescription>
              For your security, password resets are handled by our team. Get in touch and we'll help you back into your account right away.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl border border-[color:var(--hh-line)] bg-white p-3 transition-colors hover:bg-[var(--hh-band)]" data-testid="forgot-whatsapp-link">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><MessageCircle className="h-5 w-5" /></span>
              <div><div className="text-xs text-muted-foreground">Message us on WhatsApp</div><div className="font-medium text-[color:var(--hh-ink)]">07578 850395</div></div>
            </a>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Portal%20password%20reset`} className="flex items-center gap-3 rounded-xl border border-[color:var(--hh-line)] bg-white p-3 transition-colors hover:bg-[var(--hh-band)]" data-testid="forgot-email-link">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><Mail className="h-5 w-5" /></span>
              <div><div className="text-xs text-muted-foreground">Email us</div><div className="font-medium text-[color:var(--hh-ink)]">{SUPPORT_EMAIL}</div></div>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
